import * as Yup from "yup";
import { FormikForm } from "components/FormikForm/FormikForm";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Input from "components/DesignSystem/Input/Input";
import { PriceInput } from "components/PriceInput/PriceInput";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { useToast } from "hooks/useToast";
import {
  useCreateAdjustmentMutation,
  useUpdateAdjustmentMutation,
} from "store/apis/estimatedTax";
import {
  TaxAdjustment,
  CreateAdjustmentPayload,
  FinancialAdjustmentMonth,
} from "types/Models/estimatedTax";
import { BackendError } from "types/utils/error";
import { MONTH_OPTIONS } from "./constants";

const TYPE_OPTIONS = [
  { value: "ADD_BACK", label: "Add-back" },
  { value: "DEDUCTION", label: "Deduction" },
];

const validationSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  type: Yup.string().required("Type is required"),
  amount: Yup.number()
    .moreThan(0, "Amount must be greater than 0")
    .required("Amount is required")
    .typeError("Amount must be a number"),
  month: Yup.string().nullable(),
});

type FormValues = {
  title: string;
  type: string;
  amount: string;
  month: string;
  is_one_time: boolean;
  category: string;
  description: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  entityId: string;
  taxYear: string;
  adjustment: TaxAdjustment | null;
  defaultType?: "ADD_BACK" | "DEDUCTION";
  defaultMonth?: number | null;
};

export const AdjustmentFormModal = ({
  open,
  onClose,
  entityId,
  taxYear,
  adjustment,
  defaultType = "ADD_BACK",
  defaultMonth,
}: Props) => {
  const { alertToast, successToast } = useToast();

  const [createAdj, { isLoading: isCreating }] = useCreateAdjustmentMutation();
  const [updateAdj, { isLoading: isUpdating }] = useUpdateAdjustmentMutation();
  const isSaving = isCreating || isUpdating;

  const initialValues: FormValues = adjustment
    ? {
        title: adjustment.title,
        type: adjustment.type,
        amount: String(adjustment.amount),
        month: adjustment.month ?? "",
        is_one_time: adjustment.is_one_time,
        category: adjustment.category ?? "",
        description: adjustment.description ?? "",
      }
    : {
        title: "",
        type: defaultType,
        amount: "",
        month:
          defaultMonth != null
            ? MONTH_OPTIONS[defaultMonth - 1]?.value ?? ""
            : "",
        is_one_time: false,
        category: "",
        description: "",
      };

  const handleSubmit = async (values: FormValues) => {
    const payload: CreateAdjustmentPayload = {
      title: values.title,
      type: values.type as "ADD_BACK" | "DEDUCTION",
      amount: Number(values.amount),
      tax_year: Number(taxYear),
      month: (values.month as FinancialAdjustmentMonth) || null,
      is_one_time: values.is_one_time,
    };
    if (values.category) payload.category = values.category;
    if (values.description) payload.description = values.description;

    try {
      if (adjustment) {
        await updateAdj({
          entityId,
          adjustmentId: adjustment.id,
          payload,
        }).unwrap();
        successToast({ message: "Adjustment updated" });
      } else {
        await createAdj({ entityId, payload }).unwrap();
        successToast({ message: "Adjustment added" });
      }
      onClose();
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ||
          "Failed to save adjustment",
      });
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Modal.Content useCustomOverlay>
        <FormikForm<FormValues>
          key={`${adjustment?.id ?? "new"}-${defaultType}-${defaultMonth}`}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <>
              <Modal.Header>
                <Modal.Title>
                  {adjustment ? "Edit Adjustment" : "Add Adjustment"}
                </Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-space-y-4">
                <Input
                  withForm
                  name="title"
                  label="Title"
                  required
                  placeholder="e.g. Meals (50% Disallowed)"
                />

                <Combobox
                  name="type"
                  label="Type"
                  required
                  options={TYPE_OPTIONS}
                  value={
                    TYPE_OPTIONS.find((o) => o.value === values.type) ?? null
                  }
                  onChange={(option) => {
                    const opt = option as { value: string } | null;
                    setFieldValue("type", opt?.value ?? "");
                  }}
                />

                <PriceInput
                  name="amount"
                  label="Amount"
                  required
                  allowNegative={false}
                />

                <Combobox
                  name="month"
                  label="Start month"
                  options={MONTH_OPTIONS}
                  placeholder="Select month"
                  value={
                    MONTH_OPTIONS.find((o) => o.value === values.month) ?? null
                  }
                  onChange={(option) => {
                    const opt = option as { value: string } | null;
                    setFieldValue("month", opt?.value ?? "");
                  }}
                />

                <div className="t-flex t-items-center t-justify-between t-py-1">
                  <span className="t-text-body-sm t-font-medium t-text-text-100">
                    Apply to this month only
                  </span>
                  <Switch
                    checked={values.is_one_time}
                    onCheckedChange={(v) => setFieldValue("is_one_time", v)}
                  />
                </div>

                <Input
                  withForm
                  name="category"
                  label="Category"
                  placeholder="e.g. Custom Category"
                />

                <TextArea
                  name="description"
                  label="Description"
                  block
                  placeholder="Optional description"
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button disabled={isSaving}>Cancel</Button>
                </Modal.RawClose>
                <Button
                  type="submit"
                  customType="primary"
                  isLoading={isSaving}
                  disabled={isSaving}
                >
                  {adjustment ? "Save" : "Add"}
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
